import type {ToolWorkContext, ToolWorkResult, ToolParticipation, ToolParticipationPolicy, ToolParticipationRole} from "./ToolWorkLifecycleTypes";
import BaseObject from "@shared/BaseObject";
import {
	EnginePayloadData,
	ToolEngineConfigData,
	ToolEngineDataValue,
	ToolEngineValidationResult,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineDataTypes";
import type {
	BaseToolEngineInterface,
	ToolEngineInterfaceToken,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceToken";

export default class BaseToolEngine<
	TConfig extends ToolEngineConfigData = ToolEngineConfigData,
	TPayload extends EnginePayloadData = EnginePayloadData,
	TSummaryInput extends ToolEngineDataValue = ToolEngineDataValue,
> extends BaseObject {
	/**
	 * Returns the renderer-facing engine interface tokens implemented by this engine.
	 *
	 * TypeScript `implements` clauses validate method contracts at compile time, but TypeScript
	 * interfaces are erased from emitted JavaScript. The runtime therefore cannot inspect
	 * `implements ItemCollectionEngineInterface` directly.
	 *
	 * Concrete engines expose their runtime tokens here so `ToolEngineRegistry` can derive the
	 * engine interface map, validate renderer compatibility, and build the keyed `engineApi`
	 * passed to renderer components. Keep this list colocated with the engine's `implements`
	 * declaration and do not duplicate implemented-interface lists in the registry.
	 */
	getEngineInterfaceTokens(): readonly ToolEngineInterfaceToken<BaseToolEngineInterface>[] {
		throw new Error("Tool engine must implement getEngineInterfaceTokens() so the runtime can validate renderer compatibility and build engineApi.")
	}

	createDefaultPayload(config: TConfig = {} as TConfig): TPayload {
		return {} as TPayload
	}

	validateConfig(config: TConfig = {} as TConfig, definition: ToolWorkContext["definition"] = null): ToolEngineValidationResult {
		return new ToolEngineValidationResult()
	}

	normalizeConfig(config: TConfig = {} as TConfig): TConfig {
		return config || ({} as TConfig)
	}

	normalizePayload(payload: TPayload = {} as TPayload, config: TConfig = {} as TConfig): TPayload {
		return payload || this.createDefaultPayload(config)
	}

	requiresPreviousWorkPayload(config: TConfig = {} as TConfig): boolean {
		return false
	}

	getParticipation(config: TConfig = {} as TConfig): ToolParticipation {
		return {isPrivate: false, participants: []}
	}

	getParticipationPolicy(config: TConfig = {} as TConfig, definition: ToolWorkContext["definition"] = null): ToolParticipationPolicy {
		const policy = (definition as {participationPolicy?: ToolParticipationPolicy} | null)?.participationPolicy
		return policy?.roles?.length ? {roles: policy.roles.map(role => ({...role, capabilities: {...role.capabilities}}))} : {
			roles: [this.createParticipationRole("respondent", "Respondent")],
		}
	}

	createParticipationRole(id: string, title: string): ToolParticipationRole {
		return {id, title, capabilities: {canWork: true, canViewResult: false}, allowsMultipleAssignments: true}
	}

	selectParticipationPayload(payload: TPayload, config: TConfig, participantId: string): TPayload {
		return payload
	}

	getParticipationConfig(config: TConfig, participantId: string): TConfig {
		return config
	}

	getRoleConfig(config: TConfig, roleId: string, definition: ToolWorkContext["definition"] = null): TConfig {
		if (!this.getParticipationPolicy(config, definition).roles.some(role => role.id === roleId)) throw new Error(`Unknown participation role: ${roleId}`)
		return config
	}

	canAccessPublicWork(config: TConfig = {} as TConfig, isOwner = false): boolean {
		return true
	}

	canAccessPublicResult(config: TConfig = {} as TConfig): boolean {
		return true
	}

	prepareDraft(payload: TPayload, config: TConfig, context: ToolWorkContext = {}): ToolWorkResult<TPayload> {
		return this.prepareWork(payload, config, context, false)
	}

	prepareSubmission(payload: TPayload, config: TConfig, context: ToolWorkContext = {}): ToolWorkResult<TPayload> {
		return this.prepareWork(payload, config, context, true)
	}

	prepareWork(payload: TPayload, config: TConfig, context: ToolWorkContext, isSubmission: boolean): ToolWorkResult<TPayload> {
		const result: ToolWorkResult<TPayload> = {payload: JSON.parse(JSON.stringify(payload || {})), errors: [], warnings: []}
		config = JSON.parse(JSON.stringify(config || {}))
		context = JSON.parse(JSON.stringify(context || {}))
		try {
			result.payload = this.prepareWorkInput(result.payload, config, context, isSubmission)
		} catch (error) {
			result.errors.push(error instanceof Error ? error.message : String(error))
			return result
		}
		try {
			this.validateWorkInput(result.payload, config, context)
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error)
			if (isSubmission) result.errors.push(message)
			else result.warnings.push({scope: "workInput", message})
			// A draft may be incomplete or malformed, but saving it must not erase it.
			return result
		}
		try {
			// Keep the original payload available if a permissive normalizer fails midway.
			const workingPayload = JSON.parse(JSON.stringify(result.payload)) as TPayload
			const workingConfig = JSON.parse(JSON.stringify(config))
			const workingContext = JSON.parse(JSON.stringify(context))
			result.payload = this.normalizeWorkPayload(workingPayload, workingConfig, workingContext, isSubmission)
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error)
			if (this.isWorkNormalizationStrict(config)) result.errors.push(message)
			else result.warnings.push({scope: this.getWorkNormalizationScope(), message})
		}
		if (result.errors.length) return result
		try {
			result.payload = isSubmission
				? this.finalizeWorkSubmission(result.payload, config, context)
				: this.finalizeWorkDraft(result.payload, config, context)
		} catch (error) {
			result.errors.push(error instanceof Error ? error.message : String(error))
		}
		if (result.errors.length) return result
		const validationPayload = JSON.parse(JSON.stringify(result.payload))
		const validationConfig = JSON.parse(JSON.stringify(config))
		const validationContext = JSON.parse(JSON.stringify(context))
		for (const [scope, check] of [
			["toolConfig", () => this.validateConfig(validationConfig, validationContext.definition)],
			["enginePayload", () => this.validateWorkPayload(validationPayload, validationConfig, validationContext)],
		] as const) {
			try {
				const validation = check()
				if (!validation.isValid) result.warnings.push({scope, message: validation.errors[0]})
			} catch (error) {
				result.warnings.push({scope, message: error instanceof Error ? error.message : String(error)})
			}
		}
		return result
	}

	prepareWorkInput(payload: TPayload, config: TConfig, context: ToolWorkContext, isSubmission: boolean): TPayload {
		return payload
	}

	validateWorkInput(payload: TPayload, config: TConfig, context: ToolWorkContext): void {
		// Engines opt in to structural checks before normalization; semantic checks follow it.
	}

	normalizeWorkPayload(payload: TPayload, config: TConfig, context: ToolWorkContext, isSubmission: boolean): TPayload {
		return payload
	}

	finalizeWorkDraft(payload: TPayload, config: TConfig, context: ToolWorkContext): TPayload {
		return payload
	}

	finalizeWorkSubmission(payload: TPayload, config: TConfig, context: ToolWorkContext): TPayload {
		return payload
	}

	validateWorkPayload(payload: TPayload, config: TConfig, context: ToolWorkContext = {}): ToolEngineValidationResult {
		return new ToolEngineValidationResult()
	}

	isWorkNormalizationStrict(config: TConfig): boolean { return true }

	getWorkNormalizationScope(): string { return "payloadNormalization" }

	assertWorkValidation(validation: ToolEngineValidationResult): void {
		if (!validation.isValid) throw new Error(validation.errors[0])
	}

	collectWorkValidation(check: () => void): ToolEngineValidationResult {
		const result = new ToolEngineValidationResult()
		try { check() } catch (error) {
			result.errors = [error instanceof Error ? error.message : String(error)]
			result.isValid = false
		}
		return result
	}

	createSummaryInput(payload: TPayload = {} as TPayload, config: TConfig = {} as TConfig): TSummaryInput {
		return payload as ToolEngineDataValue as TSummaryInput
	}
}
